import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, MapPin, Truck, User, Clock, Package, AlertTriangle, FileCheck, QrCode } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHero } from "@/components/site/SiteBits";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";

function fmt(t) { return t ? new Date(t).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }) : "—"; }

export default function Track() {
  const [params] = useSearchParams();
  const [tn, setTn] = useState(params.get("tn") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const search = async (num) => {
    const q = (num ?? tn).trim();
    if (!q) return;
    setLoading(true); setError(""); setData(null);
    try {
      const res = await api.get(`/public/track/${encodeURIComponent(q)}`);
      setData(res.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Tracking number not found");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (params.get("tn")) search(params.get("tn")); /* eslint-disable-next-line */ }, []);

  const pct = data ? Math.round((1 - (data.distance_remaining_km || 0) / (data.distance_km || 1)) * 100) : 0;

  return (
    <div>
      <PageHero eyebrow="Real-Time Tracking" title="Track your shipment"
        subtitle="Enter your Ebik tracking number for live status, ETA and proof of delivery." />
      <section className="max-w-4xl mx-auto px-4 lg:px-8 py-12">
        <div className="flex gap-2 rounded-2xl border border-border bg-card p-3 shadow-sm">
          <Input data-testid="track-input" value={tn} onChange={(e) => setTn(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()} placeholder="e.g. EBK-2026-ZA9001" className="border-0 focus-visible:ring-0 text-base" />
          <Button data-testid="track-submit" onClick={() => search()} disabled={loading} className="bg-primary hover:bg-red-700 px-6">
            <Search size={18} className="mr-2" /> {loading ? "Searching…" : "Track"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2 font-mono">Try: EBK-2026-ZA9001 · EBK-2026-ZA9005 · EBK-2026-ZA9012</p>

        {error && <div className="mt-6 rounded-xl border border-red-200 bg-red-50 dark:bg-red-500/10 p-4 text-red-700 text-sm" data-testid="track-error">{error}</div>}

        {data && (
          <div className="mt-8 space-y-6 fade-up" data-testid="track-result">
            <div className="rounded-2xl border border-border bg-card p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="eyebrow text-muted-foreground">Tracking Number</p>
                  <h2 className="text-2xl font-heading font-extrabold font-mono">{data.tracking_number}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">{data.service_type} · {data.cargo_type}</p>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={data.status} />
                  <div className="w-16 h-16 rounded-lg bg-[#0B0F17] text-white flex items-center justify-center" title="Shipment QR">
                    <QrCode size={40} />
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="font-semibold">{data.origin}</span>
                  <span className="text-muted-foreground">{data.distance_remaining_km} km remaining</span>
                  <span className="font-semibold">{data.destination}</span>
                </div>
                <Progress value={data.status === "Delivered" ? 100 : pct} className="h-2" />
              </div>

              <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { icon: MapPin, l: "Current Location", v: data.current_location },
                  { icon: Clock, l: "Estimated Arrival", v: fmt(data.eta) },
                  { icon: Truck, l: "Vehicle", v: data.vehicle?.reg_no || "Assigning" },
                  { icon: User, l: "Driver", v: data.driver?.name || "Assigning" },
                ].map((x) => (
                  <div key={x.l} className="rounded-lg bg-muted/50 p-3">
                    <x.icon size={16} className="text-primary" />
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground mt-2">{x.l}</p>
                    <p className="text-sm font-semibold">{x.v}</p>
                  </div>
                ))}
              </div>
            </div>

            {data.exceptions?.length > 0 && (
              <div className="rounded-2xl border border-orange-200 bg-orange-50 dark:bg-orange-500/10 p-5">
                <h3 className="font-heading font-bold flex items-center gap-2 text-orange-700"><AlertTriangle size={18} /> Exceptions</h3>
                {data.exceptions.map((e, i) => (
                  <p key={i} className="mt-2 text-sm text-orange-700">{e.type}: {e.note} <span className="text-orange-500">· {fmt(e.time)}</span></p>
                ))}
              </div>
            )}

            <div className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-heading font-bold flex items-center gap-2 mb-4"><Package size={18} className="text-primary" /> Route history</h3>
              <div className="space-y-4">
                {[...(data.route_history || [])].reverse().map((r, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full ${i === 0 ? "bg-primary" : "bg-border"}`} />
                      {i < data.route_history.length - 1 && <div className="w-px flex-1 bg-border" />}
                    </div>
                    <div className="pb-2">
                      <p className="font-semibold text-sm">{r.status}</p>
                      <p className="text-sm text-muted-foreground">{r.location} · {r.note}</p>
                      <p className="text-xs text-muted-foreground font-mono mt-0.5">{fmt(r.time)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {data.pod && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-500/10 p-6">
                <h3 className="font-heading font-bold flex items-center gap-2 text-emerald-700"><FileCheck size={18} /> Proof of Delivery</h3>
                <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
                  <div><span className="text-muted-foreground">Recipient:</span> <b>{data.pod.recipient}</b></div>
                  <div><span className="text-muted-foreground">Signed:</span> <b>{fmt(data.pod.signed_at)}</b></div>
                  <div><span className="text-muted-foreground">Quantity:</span> <b>{data.pod.quantity_delivered}</b></div>
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
